import React, { useContext, useMemo } from "react";
import { Box, Checkbox, Typography } from "@mui/material";
import { ActionsMenu } from "../../../../components/ActionsMenu/ActionsMenu";
import DownloadIcon from "../../../../components/VectorIcons/DownloadIcon";
import PencilEditIcon from "../../../../components/VectorIcons/PencilEditIcon";
import ReleaseTextIcon from "../../../../components/VectorIcons/ReleaseTextIcon";
import ReleaseTextIconMarked from "../../../../components/VectorIcons/ReleaseTextIconMarked";
import DeleteIcons from "../../../ProjectSheet/ListFiles/BlockList/DeleteIcons/DeleteIcons";
import DeleteTooltip from "../../../ProjectSheet/ListFiles/DeleteTooltip/DeleteTooltip";
import { ReactComponent as ReasonIcon } from "../../../../assets/icons/reason-icon.svg";
import { PlanningGridContext } from "../PlanningGrid";
import UserContext from "../../../../context/userContext";
import useDownloadAttachment from "../../../../hooks/useDownloadAttachment";
import { CHECKBOX_SX } from "./MaterialSection.utils";
import { getLatestReleaseComment } from "../../utils";
import CustomTooltip from "../../../../components/CustomTooltip/CustomTooltip";
import { getReleaseDestinationLabel } from "../../../../utils";

export const MaterialAttachmentRow = ({
  anexo,
  versao,
  contentId,
  selectionMode,
  selectedFileIds,
  toggleOne,
  attachmentActions,
  materialVersions,
  handleOpenReleaseTexts,
  handleEditFile,
  handleDelete,
}) => {
  const { grid } = useContext(PlanningGridContext);
  const { downloadAttachment } = useDownloadAttachment();
  const userContext = useContext(UserContext);

  const {
    canDelete,
    getDeleteUnavailableMessage,
    canDownload,
    canReleaseChannel,
    canUnReleaseChannel,
    getReleaseUnavailableMessage,
    getUnReleaseUnavailableMessage,
  } = attachmentActions;

  const canDeleteThis = canDelete(anexo.id);
  const deleteUnavailableMessage = getDeleteUnavailableMessage(anexo.id);
  const canDownloadThis = canDownload(anexo.id);
  const hasReleasePermission = userContext.hasPermission({
    required: ["AttachmentRelease"],
  });

  const fileToDownload = {
    fileData: anexo,
    contentId,
    origin: "materiais",
    mode: 0,
  };

  const isReleasedToChannel = Boolean(anexo?.releaseManagement?.channel);

  const isReleasedToProduction = Boolean(anexo?.releaseManagement?.production);

  const isReleasedToCultureHub = Boolean(anexo?.releaseManagement?.cultureHub);

  const isReleased =
    isReleasedToChannel || isReleasedToProduction || isReleasedToCultureHub;

  const releaseDestination = getReleaseDestinationLabel({
    channel: isReleasedToChannel,
    production: isReleasedToProduction,
    cultureHub: isReleasedToCultureHub,
  });

  const releaseTooltip = releaseDestination
    ? `Liberado para ${releaseDestination}`
    : "";

  const canReleaseThis = isReleased
    ? canUnReleaseChannel(anexo.id)
    : canReleaseChannel(anexo.id);

  const releaseUnavailableMessage = isReleased
    ? getUnReleaseUnavailableMessage(anexo.id)
    : getReleaseUnavailableMessage(anexo.id);

  const otherVersions = useMemo(
    () =>
      (materialVersions ?? [])
        .flatMap((materialVersion) =>
          (materialVersion.attachments ?? []).map((attachment) => ({
            ...attachment,
            version: materialVersion.version,
            materialId: materialVersion.id,
            episodeNumber: materialVersion.episodeNumber,
            releaseType: "Channel",
          }))
        )
        .filter((attachment) => attachment.id !== anexo.id),
    [materialVersions, anexo.id]
  );

  return (
    <Box
      key={anexo.fileId}
      display="grid"
      gridTemplateColumns={grid}
      sx={{
        minHeight: "44px",
        bgcolor: "#E2E5FD",
        borderBottom: "1px solid #FFF",
      }}
    >
      {selectionMode && (
        <Checkbox
          checked={selectedFileIds.has(anexo.id)}
          onChange={() => toggleOne(anexo, versao)}
          onClick={(e) => e.stopPropagation()}
          size="small"
          sx={CHECKBOX_SX}
        />
      )}

      <Box
        display="flex"
        flexDirection="column"
        justifyContent="center"
        sx={{ pt: "6px", pb: "6px", pr: "12px" }}
      >
        <Typography
          sx={{
            fontFamily: "inherit",
            fontSize: "14px",
            color: "#7F808E",
            wordBreak: "break-word",
          }}
          pl={selectionMode ? "0" : "16px"}
        >
          {anexo.fileName}
        </Typography>

        {anexo.deliveryControl?.structuralRevision?.description && (
          <Box
            display="flex"
            alignItems="center"
            pl={selectionMode ? "0" : "16px"}
            sx={{ gap: "6px", mt: "4px" }}
          >
            <ReasonIcon
              aria-label="reason"
              style={{ width: "18px", height: "18px", flexShrink: 0 }}
            />
            <Typography
              sx={{
                fontFamily: "inherit",
                fontSize: "12px",
                fontWeight: "bold",
                color: "#333333",
                wordBreak: "break-word",
              }}
            >
              {anexo.deliveryControl.structuralRevision.description}
            </Typography>
          </Box>
        )}
      </Box>

      <Box />

      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-end",
          flex: "1",
          pr: "8px",
        }}
      >
        {isReleased && (
          <Box sx={{ mr: "3px", mt: "5px" }}>
            <CustomTooltip
              dataTip={releaseTooltip}
              tipLabel={
                <div className="tooltipIcon">
                  <ReleaseTextIconMarked />
                </div>
              }
              theme="light"
              position="bottom"
            />
          </Box>
        )}

        <ActionsMenu
          menuOptions={[
            {
              action: () => downloadAttachment(fileToDownload),
              label: "Baixar",
              disabled: !canDownloadThis,
              icon: <DownloadIcon color="#8C8EF5" />,
            },
            {
              action: () =>
                handleOpenReleaseTexts(
                  {
                    ...anexo,
                    version: versao.version,
                    materialId: versao.id,
                    episodeNumber: versao.episodeNumber,
                    releaseType: "Channel",
                    observation: getLatestReleaseComment(
                      anexo.releaseManagement
                    ),
                  },
                  otherVersions,
                  { type: "planningMaterial" }
                ),
              disabled: !hasReleasePermission || !canReleaseThis,
              tooltip: releaseUnavailableMessage ? (
                <DeleteTooltip message={releaseUnavailableMessage} />
              ) : null,
              label: isReleased
                ? "Liberado"
                : "Liberação",
              icon: isReleased ? (
                <ReleaseTextIconMarked />
              ) : (
                <ReleaseTextIcon />
              ),
            },
            {
              action: () =>
                handleEditFile({
                  ...anexo,
                  version: versao.version,
                  materialId: versao.id,
                  isMaterial: true,
                }),
              label: "Editar",
              icon: <PencilEditIcon />,
            },
            {
              action: () => handleDelete(anexo, versao),
              label: "Remover",
              disabled: !canDeleteThis,
              tooltip: deleteUnavailableMessage ? (
                <DeleteTooltip message={deleteUnavailableMessage} />
              ) : null,
              icon: (
                <DeleteIcons
                  returnColor={() => (canDeleteThis ? "#000" : "#CCC")}
                />
              ),
            },
          ]}
          disabled={selectionMode}
        />
      </Box>
    </Box>
  );
};
